import { Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ROWS_PER_PAGE } from "../App";
import { AppDialog } from "../components/AppDialog";
import { AppToolbar } from "../components/AppToolbar";
import { TextFieldComponent, NumberField } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import RoleUtils from "../utils/RoleUtils";

import Property from "../models/Property";
import PropertyEditDialog from "../components/Properties/PropertyEditDialog";
import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";
import { usePropertiesList } from "../hooks/usePropertiesList";

import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useToolbarActions } from "../components/Main/ToolbarActions";
import { useStyles as formStyles } from "../components/Main/formStyles";
import { Box } from "@mui/material";
import { Token } from "../theme";

export default function PropertiesList() {
  const { toolbarActionsSlot, addButtonInToolbar } = useToolbarActions();
  const { t } = useTranslation();

  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(undefined);
  const [editProperty, setEditProperty] = useState<Property | undefined>(undefined);
  const customStyles = formStyles();
  const { onDataFetch, forceUpdateOn, handleOnCreate, handleOnUpdate, handleOnDelete } = usePropertiesList();

  const handleOnDeleteProperty = () => {
    if (!selectedProperty) return;
    handleOnDelete(selectedProperty);
    setSelectedProperty(undefined);
  };

  function getAdditionalActions(instance: Property): ActionsMenuEntryIcons[] {
    if (!RoleUtils.isAdmin())
      return [
        {
          title: t("N/A"),
          action: () => {},
        },
      ];
    const options = [
      {
        title: t("Edit"),
        action: () => setEditProperty(instance),
        icon: <EditIcon />,
      },
      {
        title: t("Delete"),
        action: () => setSelectedProperty(instance),
        icon: <DeleteOutlineIcon />,
      },
    ];

    return options;
  }

  return (
    <Box sx={{ "&>div:nth-child(2)": { borderRadius: 8 } }}>
      <AppToolbar title={t("Properties")} rightContent={toolbarActionsSlot} />

      <TableBasedCrud<Property>
        headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
        buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
        cancelButtonStyles={{
          color: Token.textSecondary,
          border: `1px solid ${Token.borderDefault}`,
          marginBottom: 10,
        }}
        dialogStyles={customStyles.root}
        buttonContainer={customButtonsContainer}
        gridButtonContainer={gridButtonContainer}
        addButtonBuilder={addButtonInToolbar(customButtonBuilder)}
        elementName={"Properties"}
        rowsPerPage={ROWS_PER_PAGE}
        forceUpdateOn={forceUpdateOn}
        disableAutoFetch
        denseTable
        stickyHeader
        stickyPagination
        onDataFetch={onDataFetch}
        onCreate={(property) => handleOnCreate(property)}
        onClickEdit={(property) => setEditProperty(property)}
        resetValue={{ key: "", content: "", sinceVersion: null }}
        buildAdditionalActions={(instance) => [
          {
            title: t("Edit"),
            action: () => setEditProperty(instance),
          },
          {
            title: t("Delete"),
            action: () => setSelectedProperty(instance),
          },
        ]}
        renderDialogContent={(register, errors, control) => (
          <>
            <TextFieldComponent register={register} errors={errors} control={control} propertyName={"key"} label={"Key"} required={true} />
            <TextFieldComponent
              register={register}
              errors={errors}
              control={control}
              propertyName={"content"}
              label={"Content"}
              required={true}
            />
            <NumberField
              register={register}
              errors={errors}
              control={control}
              propertyName={"sinceVersion"}
              label={"Since version"}
              required={true}
            />
          </>
        )}
        tableColumns={[
          {
            header: t("Key"),
            content: (instance) => <>{instance.key}</>,
          },
          {
            header: t("Content"),
            content: (instance) => (
              <>{instance.content.length > 50 ? instance.content.substring(0, 100).concat("...") : instance.content}</>
            ),
          },
          {
            header: t("Since version"),
            content: (instance) => <>{instance.sinceVersion}</>,
          },
          {
            header: t("Actions"),
            content: (instance) => {
              const actions = getAdditionalActions(instance);
              return (
                <>
                  {actions.map((action) => {
                    return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
                  })}
                </>
              );
            },
          },
        ]}
        hideActions={true}
      />

      {/* Dialog to delete properties */}
      <AppDialog
        open={!!selectedProperty}
        onClose={() => setSelectedProperty(undefined)}
        title={"Delete"}
        content={
          <Typography>
            {`The next holiday is going to be eliminated: ${selectedProperty?.key}.
            Do you want to confirm this action?`}
          </Typography>
        }
        buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteProperty() }]}
      />

      <PropertyEditDialog
        editProperty={editProperty}
        onClose={() => setEditProperty(undefined)}
        onSave={(property) => handleOnUpdate(property)}
      />
    </Box>
  );
}
